/**
 * Gestión → Productos (`/gestion/productos`).
 *
 * Puerto de `Producto::combustibles`, `lubricantes` y `combos` —las tres tarjetas
 * del hub `menu/producto/menu_inicio.php`— y de las vistas
 * `views/menu/producto/{index,lubricantes,combos}.php`: **combustibles**,
 * **extras** y **combos** en una sola pantalla con pestañas, en lugar de cinco
 * accesos separados.
 *
 * Reglas conservadas:
 * - El stock del **combustible** es la **última medición del tanque**
 *   (`pts_ric.ric_physical`), no `producto.stock`; de ahí el `% TQ` y la fecha de
 *   medición que muestra la tarjeta. Para extras y combos manda `producto.stock`.
 * - El **% del tanque** es `stock / capacidad_maxima_tanque` y el **mínimo** de la
 *   tarjeta es `capacidad_minima_tanque`; cuando el físico baja del mínimo se
 *   avisa en la tarjeta.
 * - El **stock de un combo** es el mínimo de `floor(stock del componente /
 *   cantidad)` entre sus componentes.
 * - Activar/desactivar y mostrar/ocultar en el market escriben los mismos campos
 *   que el panel anterior (`producto.estado` y `producto.mostrar_en_market`).
 *
 * Diferencias anotadas:
 * - La **telemetría en vivo** de los tanques (PTS-2) no se duplica aquí: ya vive
 *   en las pantallas de **Monitoreo**; esta pantalla muestra la última toma del
 *   RIC y avisa cuando tiene días.
 * - La foto del producto (`images/productos/…`, servida por el panel anterior) se
 *   sustituye por el **punto de color** del producto.
 * - El **alta y la edición** de productos, las **categorías** con sus
 *   subcategorías y los **precios programados** se abren desde esta pantalla; la
 *   liquidación sigue pendiente y se documenta al pie. Los **proveedores** viven
 *   en *Compras → Proveedores* y la **diferencia diaria** de combustibles tiene
 * su propia pantalla en el centro de Reportes.
 */

import {
  CalendarClock,
  CircleDot,
  Eye,
  EyeOff,
  Fuel,
  History,
  Info,
  Layers,
  Lock,
  Package,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Scale,
  Search,
  Store,
  Tags,
  TriangleAlert,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';

import { ProductoFormDialog } from '../components/producto-form-dialog';
import { useCategorias } from '../productos.api';
import { CategoriasDialog, PreciosProgramadosDialog } from '../components/productos-catalogos-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';

import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatNumber, cn } from '@/lib/utils';

import {
  useCambiarEstadoProducto,
  useCambiarMarketProducto,
  useProductos,
} from '../productos.api';
import { ProductoMedicionesDialog } from '../components/producto-mediciones-dialog';
import { fechaCorta, monto } from '../gestion-formato';
import type { FiltrosProductos, ProductoGestion } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Pestañas de la pantalla (las tres tarjetas del hub + «Todos»). */
const PESTANAS = [
  { clave: 'COMBUSTIBLE', label: 'Combustibles', icono: Fuel },
  { clave: 'LUBRICANTE', label: 'Extras', icono: Package },
  { clave: 'COMBO', label: 'Combos', icono: Layers },
  { clave: 'TODOS', label: 'Todos', icono: CircleDot },
] as const;

/** Días a partir de los cuales la medición de un tanque se considera vieja. */
const DIAS_MEDICION_VIEJA = 7;

type ClavePestana = (typeof PESTANAS)[number]['clave'];

/** Tarjeta del resumen de la cabecera. */
function Resumen({
  icono: Icono,
  etiqueta,
  valor,
  detalle,
  tono,
}: {
  icono: typeof Fuel;
  etiqueta: string;
  valor: string;
  detalle: string;
  tono: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <span className={cn('grid size-10 shrink-0 place-items-center rounded-control', tono)}>
          <Icono className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">{etiqueta}</p>
          <p className="text-lg font-bold text-ink-900">{valor}</p>
          <p className="line-clamp-2 text-xs text-ink-500">{detalle}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Acciones comunes de cada producto (edición, mediciones, estado y market). */
function AccionesProducto({
  producto,
  ocupado,
  onEditar,
  onMediciones,
  onEstado,
  onMarket,
}: {
  producto: ProductoGestion;
  ocupado: boolean;
  onEditar: () => void;
  onMediciones: () => void;
  onEstado: () => void;
  onMarket: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        title="Editar producto"
        aria-label={`Editar ${producto.nombre}`}
        onClick={onEditar}
      >
        <Pencil className="size-4" />
      </Button>

      {producto.tipo === 'COMBUSTIBLE' ? (
        <Button
          variant="ghost"
          size="icon"
          title="Ver las últimas mediciones del tanque"
          aria-label={`Ver las mediciones de ${producto.nombre}`}
          onClick={onMediciones}
        >
          <History className="size-4" />
        </Button>
      ) : null}

      <Button
        variant="ghost"
        size="icon"
        disabled={ocupado}
        title={producto.estado ? 'Desactivar producto' : 'Activar producto'}
        aria-label={`${producto.estado ? 'Desactivar' : 'Activar'} ${producto.nombre}`}
        onClick={onEstado}
      >
        <Power className={cn('size-4', producto.estado ? 'text-emerald-600' : 'text-ink-300')} />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        disabled={ocupado}
        title={producto.mostrar_en_market ? 'Ocultar en el market' : 'Mostrar en el market'}
        aria-label={`${producto.mostrar_en_market ? 'Ocultar' : 'Mostrar'} ${producto.nombre} en el market`}
        onClick={onMarket}
      >
        {producto.mostrar_en_market ? (
          <Eye className="size-4 text-brand-600" />
        ) : (
          <EyeOff className="size-4 text-ink-300" />
        )}
      </Button>
    </div>
  );
}

/** Tarjeta de un combustible (equivalente a la del panel anterior). */
function TarjetaCombustible({
  producto,
  ocupado,
  onEditar,
  onMediciones,
  onEstado,
  onMarket,
}: {
  producto: ProductoGestion;
  ocupado: boolean;
  onEditar: () => void;
  onMediciones: () => void;
  onEstado: () => void;
  onMarket: () => void;
}) {
  const porcentaje = producto.porcentaje_tanque ?? 0;
  const color = producto.color ?? '#64748b';
  const medicionVieja =
    producto.dias_sin_medicion !== null && producto.dias_sin_medicion > DIAS_MEDICION_VIEJA;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-ink-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="size-3 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
          <p className="truncate font-semibold text-ink-900">{producto.nombre}</p>
          {producto.estado ? null : <Badge variant="neutral">Inactivo</Badge>}
        </div>

        <AccionesProducto
          producto={producto}
          ocupado={ocupado}
          onEditar={onEditar}
          onMediciones={onMediciones}
          onEstado={onEstado}
          onMarket={onMarket}
        />
      </div>

      <CardContent className="space-y-3 p-4">
        <div
          className="h-7 w-full overflow-hidden rounded-pill bg-ink-100"
          role="progressbar"
          aria-valuenow={porcentaje}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Nivel del tanque de ${producto.nombre}`}
        >
          <div
            className="flex h-full items-center justify-end rounded-pill px-2 text-xs font-bold text-white transition-all"
            style={{ width: `${Math.min(100, Math.max(2, porcentaje))}%`, backgroundColor: color }}
          >
            {porcentaje}%
          </div>
        </div>

        {producto.sin_medicion ? (
          <Alert variant="warning" title="Sin medición">
            Este tanque no tiene ninguna toma de RIC registrada.
          </Alert>
        ) : null}

        {producto.bajo_minimo ? (
          <p className="flex items-center gap-1.5 text-xs font-semibold text-rose-700">
            <TriangleAlert className="size-3.5" aria-hidden="true" />
            El físico está por debajo del mínimo ({formatNumber(producto.capacidad_minima_tanque ?? 0)}).
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">P. venta</p>
            <p className="text-base font-bold text-ink-900">
              {producto.precio_venta === null ? '—' : formatCurrency(producto.precio_venta)}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
              Físico {producto.unidad ? `(${producto.unidad})` : ''}
            </p>
            <p className="text-base font-bold text-ink-900">{formatNumber(producto.stock)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">Máximo</p>
            <p className="text-sm text-ink-700">{formatNumber(producto.capacidad_maxima_tanque ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">Mínimo</p>
            <p className="text-sm text-ink-700">{formatNumber(producto.capacidad_minima_tanque ?? 0)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {producto.categoria ? (
            <Badge variant="brand">
              {producto.categoria}
              {producto.subcategoria ? ` / ${producto.subcategoria}` : ''}
            </Badge>
          ) : null}
          {producto.external_code_edsuite ? (
            <span title={producto.external_code_edsuite}>
              <Badge variant="outline">Cód. {producto.external_code_edsuite.slice(0, 10)}…</Badge>
            </span>
          ) : null}
          {producto.codigo_contable ? (
            <Badge variant="outline">Contable {producto.codigo_contable}</Badge>
          ) : null}
        </div>
      </CardContent>

      <button
        className={cn(
          'flex w-full items-center justify-center gap-2 border-t border-ink-100 px-4 py-2 text-xs',
          medicionVieja ? 'bg-amber-50 text-amber-800' : 'text-ink-500 hover:bg-ink-50',
        )}
        onClick={onMediciones}
      >
        <History className="size-3.5" aria-hidden="true" />
        Medición: {fechaCorta(producto.ric_stamptime)}
        {producto.dias_sin_medicion !== null
          ? ` · hace ${producto.dias_sin_medicion} día${producto.dias_sin_medicion === 1 ? '' : 's'}`
          : ''}
      </button>
    </Card>
  );
}

export function ProductosPage() {
  useDocumentTitle('Gestión · Productos');

  const [pestana, setPestana] = useState<ClavePestana>('COMBUSTIBLE');
  const [texto, setTexto] = useState('');
  const [estado, setEstado] = useState('1');
  const [market, setMarket] = useState('');
  const [orden, setOrden] = useState('nombre');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [mediciones, setMediciones] = useState<ProductoGestion | null>(null);
  const [cambio, setCambio] = useState<{ producto: ProductoGestion; tipo: 'estado' | 'market' } | null>(
    null,
  );
  const [aviso, setAviso] = useState<string | null>(null);
  const [edicion, setEdicion] = useState<{ id: number | null; crear: boolean } | null>(null);
  const [ventana, setVentana] = useState<'categorias' | 'precios' | null>(null);

  /** Catálogos que necesitan el formulario de producto y la programación. */
  const categorias = useCategorias(ventana === 'categorias' || edicion !== null);

  const filtros = useMemo<FiltrosProductos>(
    () => ({
      search: texto.trim(),
      tipo: pestana === 'TODOS' ? '' : pestana,
      estado,
      market,
      orden,
      dir,
    }),
    [texto, pestana, estado, market, orden, dir],
  );

  const consulta = useProductos(filtros);
  const cambiarEstado = useCambiarEstadoProducto();
  const cambiarMarket = useCambiarMarketProducto();

  const productos = consulta.data?.productos ?? [];
  const resumen = consulta.data?.resumen;

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const confirmarCambio = () => {
    if (!cambio) return;

    const { producto, tipo } = cambio;

    const alTerminar = {
      onSuccess: (respuesta: { mensaje: string }) => {
        setCambio(null);
        mostrar(respuesta.mensaje);
      },
      onError: (fallo: unknown) => {
        setCambio(null);
        mostrar(fallo instanceof ApiError ? fallo.message : 'No se pudo completar el cambio.');
      },
    };

    if (tipo === 'estado') {
      cambiarEstado.mutate({ id: producto.id, estado: !producto.estado }, alTerminar);
    } else {
      cambiarMarket.mutate({ id: producto.id, mostrar: !producto.mostrar_en_market }, alTerminar);
    }
  };

  const cambiarOrden = (columna: string) => {
    if (orden === columna) {
      setDir(dir === 'asc' ? 'desc' : 'asc');

      return;
    }

    setOrden(columna);
    setDir('asc');
  };

  const flecha = (columna: string) => (orden === columna ? (dir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Productos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Combustibles con el nivel real de su tanque, extras y combos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Tags className="size-4" />}
            onClick={() => setVentana('categorias')}
          >
            Categorías
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<CalendarClock className="size-4" />}
            onClick={() => setVentana('precios')}
          >
            Precios programados
          </Button>
          <Button
            size="sm"
            leftIcon={<Plus className="size-4" />}
            onClick={() => setEdicion({ id: null, crear: true })}
          >
            Nuevo producto
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {resumen ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          <Resumen
            icono={Package}
            etiqueta="Productos"
            valor={String(resumen.total)}
            detalle={`${resumen.activos} activos · ${resumen.inactivos} inactivos`}
            tono="bg-brand-50 text-brand-600"
          />
          <Resumen
            icono={Fuel}
            etiqueta="Combustibles"
            valor={String(resumen.combustibles)}
            detalle={
              resumen.porcentaje_promedio === null
                ? 'Sin mediciones'
                : `Tanques al ${resumen.porcentaje_promedio}% de media`
            }
            tono="bg-sky-50 text-sky-600"
          />
          <Resumen
            icono={Layers}
            etiqueta="Extras y combos"
            valor={String(resumen.lubricantes + resumen.combos + resumen.extras)}
            detalle={`${resumen.lubricantes} extras · ${resumen.combos} combos`}
            tono="bg-amber-50 text-amber-600"
          />
          <Resumen
            icono={Scale}
            etiqueta="Tanques"
            valor={String(resumen.tanques)}
            detalle={
              resumen.tanques_bajo_minimo > 0
                ? `${resumen.tanques_bajo_minimo} bajo el mínimo`
                : 'Todos sobre el mínimo'
            }
            tono="bg-emerald-50 text-emerald-600"
          />
          <Resumen
            icono={Store}
            etiqueta="En market"
            valor={String(resumen.en_market)}
            detalle="Productos visibles en la tienda"
            tono="bg-ink-100 text-ink-600"
          />
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-4">
          <nav className="flex flex-wrap items-center gap-2" aria-label="Tipos de producto">
            {PESTANAS.map((opcion) => (
              <button
                key={opcion.clave}
                aria-current={pestana === opcion.clave ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center gap-2 rounded-control px-3 py-2 text-xs font-semibold transition-colors',
                  pestana === opcion.clave
                    ? 'bg-ink-900 text-white'
                    : 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700',
                )}
                onClick={() => setPestana(opcion.clave)}
              >
                <opcion.icono className="size-3.5" aria-hidden="true" />
                {opcion.label}
              </button>
            ))}

            <span className="ml-auto text-xs text-ink-500">
              {productos.length} producto{productos.length === 1 ? '' : 's'}
            </span>
          </nav>

          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-ink-500 sm:col-span-2">
              Buscar producto
              <span className="relative block">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                aria-hidden="true"
              />
              <input
                className={cn(CONTROL, 'pl-9')}
                placeholder="Buscar por nombre o barcode…"
                value={texto}
                onChange={(evento) => setTexto(evento.target.value)}
              />
                        </span>
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Estado
              <span className="relative block">
              <Select
                className={CONTROL}
                value={estado}
                onChange={(evento) => setEstado(evento.target.value)}
              >
                <option value="1">Activos</option>
                <option value="0">Inactivos</option>
                <option value="todos">Todos los estados</option>
              </Select>
                        </span>
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Market
              <span className="relative block">
              <Select
                className={CONTROL}
                value={market}
                onChange={(evento) => setMarket(evento.target.value)}
              >
                <option value="">Market: todos</option>
                <option value="1">Visibles en el market</option>
                <option value="0">Ocultos en el market</option>
              </Select>
                        </span>
            </label>
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudo cargar el catálogo">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          {consulta.isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, indice) => (
                <Skeleton key={`esqueleto-${indice}`} className="h-56 w-full" />
              ))}
            </div>
          ) : null}

          {!consulta.isLoading && productos.length === 0 ? (
            <p className="py-10 text-center text-sm text-ink-500">
              No hay productos que cumplan los filtros.
            </p>
          ) : null}

          {/* Combustibles: tarjetas con el nivel del tanque. */}
          {pestana === 'COMBUSTIBLE' && productos.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {productos.map((producto) => (
                <TarjetaCombustible
                  key={producto.id}
                  producto={producto}
                  ocupado={cambiarEstado.isPending || cambiarMarket.isPending}
                  onEditar={() => setEdicion({ id: producto.id, crear: false })}
                  onMediciones={() => setMediciones(producto)}
                  onEstado={() => setCambio({ producto, tipo: 'estado' })}
                  onMarket={() => setCambio({ producto, tipo: 'market' })}
                />
              ))}
            </div>
          ) : null}

          {/* Extras, combos y la vista completa: tabla compacta. */}
          {pestana !== 'COMBUSTIBLE' && productos.length > 0 ? (
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">
                      <button className="hover:text-brand-600" onClick={() => cambiarOrden('nombre')}>
                        Producto{flecha('nombre')}
                      </button>
                    </th>
                    <th className="px-2 py-2">Tipo / categoría</th>
                    <th className="px-2 py-2 text-right">
                      <button className="hover:text-brand-600" onClick={() => cambiarOrden('stock')}>
                        Stock{flecha('stock')}
                      </button>
                    </th>
                    <th className="px-2 py-2 text-right">
                      <button
                        className="hover:text-brand-600"
                        onClick={() => cambiarOrden('precio_venta')}
                      >
                        Venta / compra{flecha('precio_venta')}
                        <span className="block text-[10px] font-normal">S/</span>
                      </button>
                    </th>
                    <th className="px-2 py-2">Estado / market</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {productos.map((producto) => (
                    <tr key={producto.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="max-w-[11rem] px-2 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className="size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: producto.color ?? '#cbd5e1' }}
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-ink-900" title={producto.nombre}>
                              {producto.nombre}
                            </p>
                            {producto.external_code_edsuite ? (
                              <p className="truncate text-xs text-ink-500">
                                Cód. {producto.external_code_edsuite.slice(0, 10)}…
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Badge
                          variant={
                            producto.tipo === 'COMBUSTIBLE'
                              ? 'brand'
                              : producto.tipo === 'COMBO'
                                ? 'danger'
                                : 'warning'
                          }
                        >
                          {producto.tipo}
                        </Badge>
                        <p className="text-xs text-ink-500">
                          {producto.categoria ?? 'Sin categoría'}
                          {producto.subcategoria ? ` / ${producto.subcategoria}` : ''}
                        </p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <p className="font-semibold">
                          {formatNumber(
                            producto.stock_disponible === null
                              ? producto.stock
                              : producto.stock_disponible,
                          )}{' '}
                          <span className="text-xs font-normal text-ink-500">
                            {producto.unidad ?? ''}
                          </span>
                        </p>
                        {producto.stock_disponible !== null ? (
                          <p className="text-xs text-ink-500">componentes listos</p>
                        ) : producto.tipo === 'COMBUSTIBLE' ? (
                          <p className="text-xs text-ink-500">
                            {producto.sin_medicion
                              ? 'sin medición'
                              : `medición ${fechaCorta(producto.ric_stamptime)}`}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <p className="font-semibold">
                          {producto.precio_venta === null ? '—' : monto(producto.precio_venta)}
                        </p>
                        <p className="text-xs text-ink-500">
                          compra {producto.precio_compra === null ? '—' : monto(producto.precio_compra)}
                        </p>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <Badge variant={producto.estado ? 'success' : 'neutral'}>
                          {producto.estado ? 'ACTIVO' : 'INACTIVO'}
                        </Badge>
                        <p className="flex items-center gap-1 text-xs text-ink-500">
                          {producto.mostrar_en_market ? (
                            <>
                              <Eye className="size-3.5" aria-hidden="true" />
                              visible
                            </>
                          ) : (
                            <>
                              <EyeOff className="size-3.5" aria-hidden="true" />
                              oculto
                            </>
                          )}
                        </p>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex justify-end">
                          <AccionesProducto
                            producto={producto}
                            ocupado={cambiarEstado.isPending || cambiarMarket.isPending}
                            onEditar={() => setEdicion({ id: producto.id, crear: false })}
                  onMediciones={() => setMediciones(producto)}
                            onEstado={() => setCambio({ producto, tipo: 'estado' })}
                            onMarket={() => setCambio({ producto, tipo: 'market' })}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Alert variant="info" title="Pendiente de migrar en Productos">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              Asignación del producto al correlativo del controlador (`assignFuel`): va contra el
              servicio del surtidor, no contra la base.
            </span>
          </li>
        </ul>
      </Alert>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        La telemetría en vivo de los tanques está en las pantallas de Monitoreo; aquí se muestra la
        última toma del RIC y se avisa cuando tiene más de {DIAS_MEDICION_VIEJA} días.
      </p>

      <ProductoMedicionesDialog
        productoId={mediciones?.id ?? null}
        nombre={mediciones?.nombre ?? ''}
        onClose={() => setMediciones(null)}
      />

      <ProductoFormDialog
        key={edicion ? `${edicion.crear ? 'nuevo' : edicion.id}` : 'cerrado'}
        id={edicion?.id ?? null}
        crear={edicion?.crear ?? false}
        onClose={() => setEdicion(null)}
        onAviso={mostrar}
        categorias={categorias.data?.categorias ?? []}
        productos={productos}
      />

      <CategoriasDialog
        abierto={ventana === 'categorias'}
        onClose={() => setVentana(null)}
        onAviso={mostrar}
      />

      <PreciosProgramadosDialog
        abierto={ventana === 'precios'}
        onClose={() => setVentana(null)}
        onAviso={mostrar}
        productos={productos}
      />

      <ConfirmDialog
        open={cambio !== null}
        title={
          cambio?.tipo === 'market'
            ? `¿${cambio?.producto.mostrar_en_market ? 'Ocultar' : 'Mostrar'} «${cambio?.producto.nombre}» en el market?`
            : `¿${cambio?.producto.estado ? 'Desactivar' : 'Activar'} «${cambio?.producto.nombre}»?`
        }
        description={
          cambio?.tipo === 'market'
            ? 'El producto dejará de aparecer (o volverá a aparecer) en la tienda del portal.'
            : 'Un producto inactivo no se puede vender ni cargar en las notas de venta.'
        }
        confirmLabel={cambio?.tipo === 'market' ? 'Confirmar' : 'Sí, cambiar'}
        tone={cambio?.producto.estado && cambio?.tipo === 'estado' ? 'danger' : 'primary'}
        loading={cambiarEstado.isPending || cambiarMarket.isPending}
        onConfirm={confirmarCambio}
        onClose={() => setCambio(null)}
      />
    </div>
  );
}
